import type { Branded } from "@@/utilities/branded";
import { genericId } from "@@/utilities/generic-id";
import type { Except } from "type-fest";
import type { IsoAndUnixTimestamp} from "../date-time/iso-and-unix";
import { nowIsoAndUnix } from "../date-time/iso-and-unix";
import type { EntityState } from "../entity-status";
import type { OrderId } from "../orders/order-id";
import type { PrinterId } from "./printer-id";

const printerJobIdPrefix = "printer_job__";
type PrinterJobId = Branded<string, "PrinterJobId">;
const {
    factory: printerJobIdFactory,
    assert: isPrinterJobId,
    zodSchema: printerJobIdZodSchema,
} = genericId<PrinterJobId>(printerJobIdPrefix, {
    regexMatch: /(^printer_job_.*$)/,
});

type PrinterJobType =
    | "NEW_ORDER"
    | "NEW_ORDER_CONFIRMED"
    | "ORDER_WARNING"
    | "DELIVERY_SLIP";

type JobStatus = "WAITING" | "PRINTED";

type PrinterJob = {
    readonly _id: PrinterJobId;
    readonly _type: "PRINTER_JOB";

    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _status: EntityState;

    readonly printerId: PrinterId;
    readonly jobType: PrinterJobType;
    readonly jobStatus: JobStatus;

    readonly printAfter: IsoAndUnixTimestamp; //Can be used to set a later "print-date"
    readonly expiresAfter: Date;

    readonly printed?: IsoAndUnixTimestamp;
    readonly body?: string;
    readonly orderId: OrderId;
};

const printerJobFactory = (
    params: Except<PrinterJob, "_id" | "_type" | "_status" | "_createdAt">,
): PrinterJob => {
    return {
        ...params,
        _id: printerJobIdFactory(),
        _type: "PRINTER_JOB",
        _status: "ACTIVE",
        _createdAt: nowIsoAndUnix(),

        printed: undefined,
    };
};

export { isPrinterJobId, printerJobFactory, printerJobIdZodSchema };
export type { PrinterJob, PrinterJobId, PrinterJobType };
